import { useIsMobile } from "@/hooks/use-mobile";
import { cdnVideoSrc } from "@/lib/cdnVideo";
import { useCallback, useEffect, useRef, useState } from "react";

const videos = [
    {
        src: "https://cdn.pixro.ai/2ac9f5e2-6342-40da-bedd-935aba515f2c.mp4",
        ratio: "9 / 16",
    },
    {
        src: "https://cdn.pixro.ai/cbe014af-5b81-4299-9888-a9798e132604.mp4",
        ratio: "9 / 16",
    },
    {
        src: "https://cdn.pixro.ai/7daf19ae-761c-480b-97d9-1cf92ccafcc1.mp4",
        ratio: "9 / 16",
    },
    {
        src: "https://cdn.pixro.ai/6648fb5f-0358-40af-9890-13f66add06b1.mp4",
        ratio: "9 / 16",
    },
    {
        src: "https://cdn.pixro.ai/2162fd98-90f7-42fa-99d1-cfecb06fa5a8.mp4",
        ratio: "9 / 16",
    },
    {
        src: "https://cdn.pixro.ai/2bf7eb1a-8646-41f7-b2c8-682040bdd68a.mp4",
        ratio: "9 / 16",
    },
    {
        src: "https://cdn.pixro.ai/6cec104d-7ba5-4796-9c12-5998711e78fd.mp4",
        ratio: "9 / 16",
    },
    {
        src: "https://cdn.pixro.ai/ee1a16c5-ee12-4120-a667-7a595e68fa76.mp4",
        ratio: "9 / 16",
    },
    {
        src: "https://cdn.pixro.ai/86d78a9d-68bb-4ee8-87ae-15a30b9f457b.mp4",
        ratio: "9 / 16",
    },
    {
        src: "https://cdn.pixro.ai/94e026f0-9e9a-4244-ad5e-933960cf130e.mp4",
        ratio: "9 / 16",
    },
    {
        src: "https://cdn.pixro.ai/55f56831-750f-4314-bb35-5db6d4305785.mp4",
        ratio: "3 / 4",
    },
    {
        src: "https://cdn.pixro.ai/ca88ebeb-babe-4903-a46a-a16245743083.mp4",
        ratio: "9 / 16",
    },
    {
        src: "https://cdn.pixro.ai/909fd669-c054-427d-9dac-cbb170445bbe.mp4",
        ratio: "9 / 16",
    },
    {
        src: "https://cdn.pixro.ai/13359561-d06b-49fd-84e4-a4c265d45b23.mp4",
        ratio: "9 / 16",
    },
    {
        src: "https://cdn.pixro.ai/95e32103-936d-4f73-9d76-f27a1b3d4baa.mp4",
        ratio: "9 / 16",
    },
];

const N = videos.length; // 15
const MID = Math.floor(N / 2); // 7 — middle video index
const START = N + MID; // start in middle copy, centered on middle video
const ALL = [...videos, ...videos, ...videos];

/**
 * Carousel video that only downloads its source once it scrolls near the
 * viewport. The carousel renders the 15 clips ×3 (infinite-loop trick), so
 * eagerly mounting raw <video> elements made the browser fetch every clip —
 * including the same 14.8 MB file multiple times — on first paint. Gating the
 * <source> behind an IntersectionObserver (with preload="none") means only the
 * handful of on-screen cards ever load, cutting first-load payload massively.
 */
const CarouselVideo = ({ src, unmuted }: { src: string; unmuted: boolean }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [load, setLoad] = useState(false);
    const isMobile = useIsMobile();

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        if (typeof IntersectionObserver === "undefined") {
            setLoad(true);
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setLoad(true);
                    observer.disconnect();
                }
            },
            { rootMargin: "150px" },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!load || !videoRef.current) return;
        const attempt = videoRef.current.play();
        if (attempt && typeof attempt.catch === "function") {
            attempt.catch(() => {});
        }
    }, [load]);

    return (
        <video
            ref={videoRef}
            autoPlay
            loop
            muted={!unmuted}
            playsInline
            preload="none"
            className="absolute inset-0 w-full h-full object-cover"
        >
            {load && (
                <>
                    {/* Device-appropriate variant first; original as fallback. */}
                    {cdnVideoSrc(src, isMobile) !== src && (
                        <source
                            src={cdnVideoSrc(src, isMobile)}
                            type="video/mp4"
                        />
                    )}
                    <source
                        src={src}
                        type={
                            src.endsWith(".webm") ? "video/webm" : "video/mp4"
                        }
                    />
                </>
            )}
        </video>
    );
};

const getScrollTarget = (
    track: HTMLDivElement,
    inner: HTMLDivElement,
    index: number,
) => {
    const card = inner.children[index] as HTMLElement | undefined;
    if (!card) return 0;
    return card.offsetLeft + card.offsetWidth / 2 - track.clientWidth / 2;
};

const HowItWorks = () => {
    const [active, setActive] = useState(START);
    const sectionRef = useRef<HTMLElement | null>(null);
    const [inView, setInView] = useState(false);
    const trackRef = useRef<HTMLDivElement | null>(null);
    const innerRef = useRef<HTMLDivElement | null>(null);
    const pausedRef = useRef(false);
    const scrollAccRef = useRef(0);
    const [unmutedKey, setUnmutedKey] = useState<string | null>(null);

    const realActive = active % N;

    const toggleMute = useCallback((index: number) => {
        setUnmutedKey((prev) =>
            prev === String(index) ? null : String(index),
        );
    }, []);

    useEffect(() => {
        const node = sectionRef.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            ([entry]) => setInView(entry.isIntersecting),
            { threshold: 0.2 },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    // Continuous marquee auto-scroll. scrollLeft is integer-rounded, so a sub-1px
    // per-frame speed is accumulated and applied in whole-pixel steps. The track
    // renders the clips ×3; once we pass the second copy we jump back by one set
    // for a seamless infinite loop. The nearest-centered card drives the active
    // dot. Paused while off-screen or on hover/touch.
    useEffect(() => {
        const track = trackRef.current;
        const inner = innerRef.current;
        if (!track || !inner) return;
        const SPEED = 0.5;
        let raf = 0;
        const tick = () => {
            if (!pausedRef.current && inView) {
                const oneSet = inner.scrollWidth / 3;
                if (oneSet > 0) {
                    scrollAccRef.current += SPEED;
                    const step = Math.floor(scrollAccRef.current);
                    if (step >= 1) {
                        scrollAccRef.current -= step;
                        let next = track.scrollLeft + step;
                        if (next >= oneSet * 2) next -= oneSet;
                        track.scrollLeft = next;
                    }
                    const trackCenter =
                        track.scrollLeft + track.clientWidth / 2;
                    let closest = 0;
                    let bestDist = Infinity;
                    for (let i = 0; i < inner.children.length; i++) {
                        const card = inner.children[i] as HTMLElement;
                        const center = card.offsetLeft + card.offsetWidth / 2;
                        const dist = Math.abs(center - trackCenter);
                        if (dist < bestDist) {
                            bestDist = dist;
                            closest = i;
                        }
                    }
                    setActive((prev) => (prev === closest ? prev : closest));
                }
            }
            raf = window.requestAnimationFrame(tick);
        };
        raf = window.requestAnimationFrame(tick);
        return () => window.cancelAnimationFrame(raf);
    }, [inView]);

    useEffect(() => {
        const track = trackRef.current;
        const inner = innerRef.current;
        if (!track || !inner) return;

        const doScroll = () => {
            track.scrollTo({
                left: getScrollTarget(track, inner, START),
                behavior: "auto",
            });
        };

        // Wait for layout + video metadata so offsetLeft/offsetWidth are accurate
        const id = setTimeout(doScroll, 300);
        return () => clearTimeout(id);
    }, []);

    const handleDotClick = (realIndex: number) => {
        const track = trackRef.current;
        const inner = innerRef.current;
        if (!track || !inner) return;
        const target = realIndex + N;
        track.scrollLeft = getScrollTarget(track, inner, target);
        setActive(target);
    };

    return (
        <section
            ref={sectionRef}
            className="pt-2 pb-12 sm:pt-3 sm:pb-16 md:pt-4 md:pb-24 overflow-hidden"
            onMouseEnter={() => {
                pausedRef.current = true;
            }}
            onMouseLeave={() => {
                pausedRef.current = false;
            }}
            onTouchStart={() => {
                pausedRef.current = true;
            }}
            onTouchEnd={() => {
                pausedRef.current = false;
            }}
            aria-label="Showcase video carousel"
        >
            <h2 className="sr-only">Pixro AI Showcase</h2>
            <div
                ref={trackRef}
                className="relative w-full overflow-x-auto scrollbar-hide"
                style={{ WebkitOverflowScrolling: "touch" }}
            >
                <div
                    ref={innerRef}
                    className="flex items-center gap-4 md:gap-6 px-[10vw] md:px-[22.5vw] lg:px-[27.5vw]"
                >
                    {ALL.map((v, i) => {
                        const isUnmuted = unmutedKey === String(i);
                        return (
                            <button
                                key={i}
                                type="button"
                                onClick={() => handleDotClick(i % N)}
                                className="group relative shrink-0 overflow-hidden rounded-[28px] bg-[#141718] h-[432px] focus:outline-none"
                                style={{ aspectRatio: v.ratio }}
                                aria-current={i === active ? "true" : undefined}
                                aria-label={`Showcase video ${(i % N) + 1}`}
                            >
                                <CarouselVideo
                                    src={v.src}
                                    unmuted={isUnmuted}
                                />
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleMute(i);
                                    }}
                                    className="absolute bottom-3 right-3 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 backdrop-blur-sm hover:bg-black/80"
                                    aria-label={
                                        isUnmuted
                                            ? "Mute video"
                                            : "Unmute video"
                                    }
                                >
                                    {isUnmuted ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                                            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                                            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                                        </svg>
                                    ) : (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                                            <line
                                                x1="23"
                                                y1="9"
                                                x2="17"
                                                y2="15"
                                            />
                                            <line
                                                x1="17"
                                                y1="9"
                                                x2="23"
                                                y2="15"
                                            />
                                        </svg>
                                    )}
                                </button>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* dots — the button provides a ≥44px tap target (a11y); the visual
                stays a thin pill via the inner <span> */}
            <div className="mt-4 flex items-center justify-center">
                {videos.map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => handleDotClick(i)}
                        aria-label={`Go to video ${i + 1}`}
                        aria-current={i === realActive}
                        className="group flex h-11 w-6 items-center justify-center"
                    >
                        <span
                            className={`block h-1.5 rounded-full transition-all duration-300 ${
                                i === realActive
                                    ? "w-6 bg-foreground"
                                    : "w-1.5 bg-foreground/30 group-hover:bg-foreground/50"
                            }`}
                        />
                    </button>
                ))}
            </div>
        </section>
    );
};

export default HowItWorks;
